import { AppLink } from "@/components/AppLink/AppLink";
import { HelpArticle } from "@/components/HelpArticle/HelpArticle";
import { Screenshot } from "@/components/Screenshot/Screenshot";
import { createFileRoute } from "@tanstack/react-router";
import vitecMarkedOgTjenester from "./vitec-marked-og-tjenester.png";
import vitecBestillTjeneste from "./vitec-bestill-tjeneste.png";
import vitecVelgAliceBob from "./vitec-velg-alice-bob.png";
import oppdrag from "./oppdrag.png";
import leggTilDokument from "./legg-til-dokument.png";
import nyttSkjøte from "./nytt-skjote.png";
import lagreMedValideringsfeil from "./lagre-med-valideringsfeil.png";
import sendTilSignering from "./send-til-signering.png";
import signeresAv from "./signeres-av.png";
import signeringsportalen from "./signeringsportalen.png";
import tinglysValgte from "./tinglys-valgte.png";
import tinglyst from "./tinglyst.png";

const title = "Kom i gang med elektronisk tinglysing";

export const Route = createFileRoute("/_authenticated-user/hjelp/kom-i-gang/")({
  head: () => ({
    meta: [{ title: `${title} | Alice & Bob` }],
  }),
  component: Page,
});

function Page() {
  return (
    <HelpArticle title={title}>
      <p>
        Denne veiledningen viser hvordan du bestiller tinglysing fra Vitec, oppretter et tinglysingsdokument, sender det
        til signering og sender det inn til tinglysing. Vi bruker et nytt skjøte som eksempel.
      </p>

      <h2>1. Opprett oppdraget i Vitec</h2>
      <p>Opprett oppdraget i Vitec som vanlig.</p>

      <h2>2. Bestill Alice & Bob – Tinglysing</h2>
      <p>For å e-tinglyse dokumenter velger du «Marked og tjenester» på oppdraget i Vitec.</p>
      <Screenshot
        src={vitecMarkedOgTjenester}
        width={318}
        alt="Oppdragsmenyen i Vitec med «Marked og tjenester» markert."
      />

      <p>Trykk deretter på «Bestill tjeneste».</p>
      <Screenshot
        src={vitecBestillTjeneste}
        width={403}
        alt="Siden «Marked og tjenester» i Vitec med knappen «Bestill tjeneste» markert."
      />

      <p>Velg «Alice & Bob – Tinglysing» i listen over tjenester.</p>
      <Screenshot
        src={vitecVelgAliceBob}
        width={477}
        alt="Dialogen «Bestill tjeneste» i Vitec med «Alice & Bob – Tinglysing» øverst i listen."
      />

      <h2>3. Velg dokument</h2>
      <p>Standard ønsket tinglysingsmetode er elektronisk på alle oppdrag.</p>
      <Screenshot
        src={oppdrag}
        width={489}
        alt="Et nytt oppdrag i Alice & Bob uten dokumenter, med knappen «Legg til dokument»."
      />

      <p>Velg deretter hvilket dokument du ønsker å tinglyse under «Legg til dokument».</p>
      <Screenshot
        src={leggTilDokument}
        width={581}
        alt="Menyen «Legg til dokument» åpen, med valgene skjøte, hjemmelserklæring, pantedokument fra bank, sikring og sletting."
      />

      <h2>4. Fyll ut dokumentet</h2>
      <p>Følg flyten i systemet og fyll inn informasjon som mangler.</p>
      <p>
        Mye av informasjonen i dokumentet er forhåndsutfylt basert på data hentet fra Vitec. Du kan likevel redigere og
        oppdatere opplysningene der det er relevant. Alle hvite felt kan redigeres.
      </p>
      <Screenshot
        src={nyttSkjøte}
        width={900}
        alt="Skjemaet «Nytt skjøte» med overdragelse, kjøpesum og salgsverdi forhåndsutfylt fra Vitec. Partene står under «Fra» og «Til» med knappen «Velg parter», og feltene for andel, brukstype, boligtype, beløp og omsetningstype er hvite og kan redigeres."
      />

      <h2>5. Lagre dokumentet</h2>
      <p>
        Huk av for «Lagre selv med valideringsfeil fra Kartverket» om nødvendig. Da får du lagret dokumentet selv om
        Kartverket melder om feil. Det er nyttig for eksempel når man forbereder et skjøte uten at hjemmelserklæring er
        tinglyst, eller hvis dokumentet krever vedlegg.
      </p>
      <Screenshot
        src={lagreMedValideringsfeil}
        width={460}
        alt="Avkrysningsboksen «Lagre selv med valideringsfeil fra Kartverket» avkrysset, over knappene «Lagre» og «Avbryt»."
      />
      <p>
        Les mer om hva valideringsfeilene betyr i{" "}
        <AppLink to="/hjelp/valideringsfeil-fra-kartverket" variant="underline">
          Valideringsfeil fra Kartverket
        </AppLink>
        .
      </p>

      <h2>6. Send til signering</h2>
      <p>
        Du kan nå sende skjøtet til signering dersom det ikke er behov for hjelpedokumenter eller signaturer fra
        personer utenfor oppdraget.
      </p>
      <p>Ved behov kan du også redigere eller forhåndsvise dokumentet før du sender det til signering.</p>
      <Screenshot
        src={sendTilSignering}
        width={900}
        alt="Et oppdrag med et skjøte, knappene «Send til signering» og «Vis», og menyen bak de tre prikkene åpen."
      />

      <h2>7. Signeringslenken</h2>
      <p>
        Signeringslenken vil alltid være den samme, og kan hentes opp igjen på oppdraget i Alice & Bob. Den sendes til
        signatarene på SMS via Vitec.
      </p>
      <Screenshot
        src={signeresAv}
        width={689}
        alt="Dialogen «Signeres av» med signatarene som ennå ikke har signert, og lenken til signeringsportalen."
      />

      <p>
        Signatarene logger inn med BankID, og alle dokumenter som skal signeres vil dukke opp i portalen. Dersom det er
        flere ulike skjøter en utbygger skal signere, vil samtlige finnes under samme lenke.
      </p>
      <Screenshot
        src={signeringsportalen}
        width={624}
        alt="Signeringsportalen med et skjøte under «Dokumenter til signering» med knappen «Signer med BankID», og to dokumenter under «Ferdig signerte dokumenter»."
      />

      <h2>8. Send inn til tinglysing</h2>
      <p>
        Når alle har signert, kan du huke av dokumentet og trykke «Tinglys valgte» for å sende det inn til tinglysing.
      </p>
      <Screenshot
        src={tinglysValgte}
        width={900}
        alt="Et skjøte med statusen «Alle har signert» som er huket av, og knappen «Tinglys valgte» aktiv."
      />

      <h2>9. Dokumentet er tinglyst</h2>
      <p>Straks etter innsendelse er skjøtet tinglyst.</p>
      <Screenshot
        src={tinglyst}
        width={900}
        alt="Skjøtet med statusen «Tinglyst» og en forsendelse med meldingen «Meldingen er tinglyst i grunnboken.» og lenke til grunnboksutskrift."
      />
    </HelpArticle>
  );
}
